import React, { useMemo } from "react";
import { LdsButton } from "@elilillyco/ux-lds-react";
import "./StepTracker.scss";

/**
 * StepTracker Component
 * Displays a dynamic progress tracker with checkboxes and connecting lines (horizontal layout)
 * Automatically generates steps based on list type (MLR or Salesforce)
 * 
 * @param {String} listType - "MLR" or "SALESFORCE" (determines workflow steps)
 *                            - MLR: 4 steps (Draft → Pending Reviewer → Pending Approver → Approved)
 *                            - Salesforce: 3 steps (Draft → Pending Approver → Approved)
 * @param {String} currentStatus - Current status of the list (e.g., "DRAFT", "REQUEST FOR REVIEW", etc.)
 *                                 MLR statuses: "DRAFT", "REQUEST FOR REVIEW", "PENDING ROUTING FOR APPROVAL", "REQUEST FOR APPROVAL", "APPROVED"
 *                                 Salesforce statuses: "DRAFT", "REQUEST FOR APPROVAL", "APPROVED"
 * @param {Array} steps - Optional: Custom array of step objects with { label: string, completed: boolean }
 *                        If provided, overrides automatic step generation
 * @param {Function} onRemind - Callback function when Remind button is clicked
 * @param {Function} isCooldownActive - Function to check if cooldown is active for a status (returns boolean)
 * @param {Object} specialtyList - Specialty list data (includes reviewerlist, approverlist, reviewsubmitted, approversubmitted)
 * @param {Array} userTableList - List of all users (for email lookup)
 * @param {Boolean} loadingLists - Whether reviewer/approver lists are loading
 * 
 * @example
 * // MLR list workflow
 * <StepTracker listType="MLR" currentStatus="REQUEST FOR REVIEW" />
 * 
 * @example
 * // Salesforce list workflow
 * <StepTracker listType="SALESFORCE" currentStatus="REQUEST FOR APPROVAL" />
 * 
 * @example
 * // Custom steps (legacy usage)
 * <StepTracker steps={[
 *   { label: "Step 1", completed: true },
 *   { label: "Step 2", completed: false }
 * ]} />
 */
// Status order for MLR workflow
const MLR_WORKFLOW = [
  { status: "DRAFT", label: "Draft", showRemind: false },
  { status: "REQUEST FOR REVIEW", label: "Request For Review", showRemind: true },
  { status: "PENDING ROUTING FOR APPROVAL", label: "Request For Approval", showRemind: false }, // Hidden intermediate status
  { status: "REQUEST FOR APPROVAL", label: "Pending For Approver", showRemind: true },
  { status: "APPROVED", label: "List Approved", showRemind: false }
];

// Status order for Salesforce workflow
const SALESFORCE_WORKFLOW = [
  { status: "DRAFT", label: "Draft", showRemind: false },
  { status: "REQUEST FOR APPROVAL", label: "Request For Approval", showRemind: true },
  { status: "APPROVED", label: "List Approved", showRemind: false }
];

const StepTracker = ({ listType, currentStatus, steps, onRemind, isCooldownActive, specialtyList, userTableList, loadingLists }) => {

  /**
   * Generate steps dynamically based on list type and current status
   */
  const generatedSteps = useMemo(() => {
    // If custom steps are provided, use them
    if (steps && steps.length > 0) {
      return steps;
    }

    // Determine workflow based on list type
    const workflow = listType === "MLR" ? MLR_WORKFLOW : SALESFORCE_WORKFLOW;

    // Normalize current status to uppercase for comparison
    const normalizedStatus = (currentStatus || "").toUpperCase();

    // Find the index of the current status in the full workflow
    const currentWorkflowIndex = workflow.findIndex(step => step.status === normalizedStatus);

    // Generate visible steps (exclude PENDING ROUTING FOR APPROVAL from display)
    const visibleSteps = workflow.filter(step => step.status !== "PENDING ROUTING FOR APPROVAL");

    // Map each visible step to include completion status and current status
    return visibleSteps.map((step, visibleIndex) => {
      // Find the actual index of this visible step in the full workflow
      const workflowIndex = workflow.findIndex(w => w.status === step.status);

      // A step is completed if the current workflow index is GREATER than this step's workflow index
      // Special case: if status is PENDING ROUTING FOR APPROVAL, mark up to REQUEST FOR REVIEW as completed
      const isCompleted = currentWorkflowIndex > workflowIndex ||
                         (normalizedStatus === "PENDING ROUTING FOR APPROVAL" && 
                          step.status === "REQUEST FOR REVIEW");

      // Identify the current step (matching the current status)
      const isCurrent = currentWorkflowIndex === workflowIndex;

      return {
        label: step.label,
        status: step.status,
        completed: isCompleted,
        current: isCurrent,
        showRemind: step.showRemind || false
      };
    });
  }, [listType, currentStatus, steps]);

  /**
   * Check if there are pending users for the current status
   */
  const hasPendingUsers = (status) => {
    if (!specialtyList) return true; // Default to true if no list data
    
    if (status === "REQUEST FOR REVIEW") {
      const reviewerList = specialtyList.reviewerlist || [];
      const reviewSubmitted = specialtyList.reviewsubmitted || [];
      // There are pending users if not everyone has submitted
      return reviewerList.length > reviewSubmitted.length;
    } else if (status === "REQUEST FOR APPROVAL") {
      const approverList = specialtyList.approverlist || [];
      const approverSubmitted = specialtyList.approversubmitted || [];
      // There are pending users if not everyone has submitted
      return approverList.length > approverSubmitted.length;
    }
    
    return true; // Default to true for other statuses
  };

  /**
   * Determine if the Remind button should be visible for a step
   */
  const shouldShowRemindButton = (step) => {
    // Must be configured to show the Remind button
    if (!step.showRemind) return false;
    
    // Must be the current step
    if (!step.current) return false;
    
    // Cooldown must not be active
    if (isCooldownActive && isCooldownActive(step.status)) return false;
    
    // Must have pending users
    if (!hasPendingUsers(step.status)) return false;
    
    return true;
  };

  return (
    <>
    <div className="step-tracker-container">
      {generatedSteps.map((step, index) => (
        <div key={index} className="step-tracker-item">
          <div className="step-tracker-content">
            {/* Circle wrapper (Remind button + Checkbox stacked) */}
            <div className="step-tracker-circle-wrapper">
              {shouldShowRemindButton(step) && (
                <LdsButton
                  classes="step-remind-btn"
                  size="small"
                  onClick={() => onRemind && onRemind(step.status)}
                >
                  Remind
                </LdsButton>
              )}
              {/* Checkbox/Circle */}
              <div
                className={`step-tracker-checkbox ${
                  step.completed ? "completed" : step.current ? "current" : "pending"
                }`}
              >
                {step.completed ? (
                  <svg width="2.5rem" height="2.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="12" fill="#007a33" />
                    <path d="M7 12.5L10.5 16L17 9" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : step.current ? (
                  <svg width="2.5rem" height="2.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="12" fill="#fbc02d" />
                    <path d="M12 4C9.2 4 7 6.2 7 9V12.5L5.5 15.5H18.5L17 12.5V9C17 6.2 14.8 4 12 4Z" fill="#000" />
                    <path d="M10 16.5C10 17.6 10.9 18.5 12 18.5C13.1 18.5 14 17.6 14 16.5" fill="#000" />
                    <circle cx="17" cy="7" r="2.5" fill="#e53935" />
                  </svg>
                ) : (
                  <span className="step-number">{index + 1}</span>
                )}
              </div>
            </div>

            {/* Connecting Line (to the right) */}
            {index < generatedSteps.length - 1 && (
              <div
                className={`step-tracker-line ${
                  step.completed && generatedSteps[index + 1]?.completed
                    ? "completed"
                    : step.completed && generatedSteps[index + 1]?.current
                    ? "completed"
                    : step.completed
                    ? "partially-completed"
                    : "pending"
                }`}
              />
            )}
          </div>
          

          {/* Step Label */}
          <div className="step-tracker-label">
            {step.label}
          </div>
        </div>
          
      ))}
    </div>
    <div className="step-tracker-user-lists">
      {loadingLists ? (
        <p style={{ color: "#666", fontStyle: "italic", padding: "0 1rem" }}>Loading reviewer &amp; approver lists...</p>
      ) : (
        <>
      {specialtyList?.reviewerlist && specialtyList.reviewerlist.length > 0 && (
        <div className="step-tracker-user-section">
          <span><b>Reviewer List:</b></span>
          <ul>
            {specialtyList.reviewerlist.map((value, index) => (
              <li key={`reviewer-${index}-${value}`} style={{ listStyleType: "none" }}>
                <span>
                  &nbsp;
                  <b style={{ color: specialtyList.reviewsubmitted?.includes(value) ? "#31855b" : null }}>
                    {userTableList?.find((elm) => elm.uuid === value)?.email_id}
                  </b>
                  &nbsp;
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {specialtyList?.reviewerlist && specialtyList.reviewerlist.length > 0 &&
       specialtyList?.approverlist && specialtyList.approverlist.length > 0 && (
        <div className="step-tracker-divider" />
      )}
      {specialtyList?.approverlist && specialtyList.approverlist.length > 0 && (
        <div className="step-tracker-user-section">
          <span><b>Approver List:</b></span>
          <ul>
            {specialtyList.approverlist.map((value, index) => (
              <li key={`approver-${index}-${value}`} style={{ listStyleType: "none" }}>
                <span>
                  &nbsp;
                  <b style={{ color: specialtyList.approversubmitted?.includes(value) ? "#31855b" : null }}>
                    {userTableList?.find((elm) => elm.uuid === value)?.email_id}
                  </b>
                  &nbsp;
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
        </>
      )}
    </div>
    </>
  );
};

export default StepTracker;